import { useRef, useState } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { X, Plus } from 'lucide-react';
import { RESPITE_PATTERNS } from '../lib/orgTypes';

/**
 * Short-break (respite) carers on one placement.
 *
 * The *people* live once in the user-level pool (`User.respite_carers`), so
 * the same family can be attached to several placements; the *stays* are
 * entries on the placement: `{ carer_id, pattern, from, to, notes }`.
 *
 * - Type a name; Enter or "Add" creates a pool entry AND attaches it here.
 * - As you type, pool carers not yet attached appear as one-tap suggestions.
 * - Each attached carer is a row with a "how often" pattern and an X.
 *
 * Props:
 *  - attached: respite entries for this placement
 *  - pool: the user's respite carers
 *  - onChange({ attached, pool }) — pool is returned whole so the caller can
 *    persist a newly created carer via PUT /profile
 */
export const RespiteCarerPicker = ({
  attached = [],
  pool = [],
  onChange,
  hint = 'Did anyone else look after you for short breaks — weekends, school holidays, or while your carers were away? Add them here. You can link the same people from your other placements too.',
  testId = 'respite-carers',
}) => {
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  const attachedIds = attached.map((e) => e.carer_id);
  const rows = attached
    .map((e) => ({ entry: e, carer: pool.find((c) => c.id === e.carer_id) }))
    .filter((r) => r.carer);

  const trimmed = draft.trim();
  const lower = trimmed.toLowerCase();
  const suggestions = trimmed
    ? pool.filter((c) => !attachedIds.includes(c.id) && c.name.toLowerCase().includes(lower)).slice(0, 6)
    : [];
  const exactMatch = trimmed ? pool.find((c) => c.name.toLowerCase() === lower) : null;

  const emit = (nextAttached, nextPool = pool) => onChange?.({ attached: nextAttached, pool: nextPool });

  const detach = (carerId) => emit(attached.filter((e) => e.carer_id !== carerId));
  const setPattern = (carerId, pattern) =>
    emit(attached.map((e) => (e.carer_id === carerId ? { ...e, pattern } : e)));

  const attachExisting = (carer) => {
    if (!attachedIds.includes(carer.id)) {
      emit([...attached, { carer_id: carer.id, pattern: null, from: null, to: null, notes: null }]);
    }
    setDraft('');
    inputRef.current?.focus();
  };

  const createAndAttach = () => {
    if (!trimmed) return;
    if (exactMatch) return attachExisting(exactMatch);
    const newCarer = {
      id: (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `rc_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      name: trimmed,
      carer_names: [],
      kind: 'carers',
    };
    emit([...attached, { carer_id: newCarer.id, pattern: null, from: null, to: null, notes: null }], [...pool, newCarer]);
    setDraft('');
    inputRef.current?.focus();
  };

  return (
    <div className="space-y-3">
      {hint && <p className="text-sm text-ink-55 leading-relaxed">{hint}</p>}
      <div className="flex gap-2">
        <Input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); createAndAttach(); } }}
          placeholder="A name, or a family name"
          className="flex-1"
          data-testid={`${testId}-input`}
        />
        <Button type="button" variant="secondary" onClick={createAndAttach} disabled={!trimmed} data-testid={`${testId}-add`}>
          Add
        </Button>
      </div>

      {suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testId}-suggestions`}>
          <span className="text-sm text-ink-55 self-center pr-1">Already known:</span>
          {suggestions.map((c) => (
            <button
              type="button"
              key={c.id}
              onClick={() => attachExisting(c)}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-dashed border-brand text-brand text-sm hover:bg-brand-wash transition-colors duration-state"
              data-testid={`${testId}-suggestion-${c.id}`}
            >
              <Plus className="w-3 h-3" aria-hidden="true" />
              {c.name}
            </button>
          ))}
        </div>
      )}

      {rows.length > 0 && (
        <ul className="space-y-2" data-testid={`${testId}-rows`}>
          {rows.map(({ entry, carer }) => (
            <li key={carer.id} className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-md bg-inset px-3 py-2">
              <span className="flex-1 text-[15px] font-medium text-ink">
                {carer.name}
                {carer.kind === 'childrens_home' && <span className="text-sm text-ink-55 font-normal"> · children&apos;s home</span>}
              </span>
              <Select value={entry.pattern || ''} onValueChange={(v) => setPattern(carer.id, v)}>
                <SelectTrigger className="sm:w-[220px] h-9 bg-surface border-surface" aria-label={`How often with ${carer.name}`} data-testid={`${testId}-pattern-${carer.id}`}>
                  <SelectValue placeholder="How often?" />
                </SelectTrigger>
                <SelectContent>
                  {RESPITE_PATTERNS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <button
                type="button"
                onClick={() => detach(carer.id)}
                className="rounded-full hover:bg-rule p-1 transition-colors duration-state self-end sm:self-auto"
                aria-label={`Remove ${carer.name} from this placement`}
                data-testid={`${testId}-remove-${carer.id}`}
              >
                <X className="w-3.5 h-3.5" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default RespiteCarerPicker;
